import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { FiMail, FiExternalLink, FiInfo, FiHeart, FiAlertTriangle } = FiIcons;

const Section = ({ children, className = "" }) => (
  <section className={`section-container ${className}`}>
    {children}
  </section>
);

function App() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  return (
    <div className="min-h-screen selection:bg-sage-200">
      {/* Hero Section */}
      <header className="bg-[#F3F1ED] border-b border-[#E8E6E1]">
        <Section className="text-center py-20 md:py-32">
          <motion.div {...fadeIn}>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#8C8279] mb-6 font-medium">
              <span className="w-8 h-[1px] bg-[#8C8279]"></span>
              Status Update
              <span className="w-8 h-[1px] bg-[#8C8279]"></span>
            </div>
            <h1 className="text-4xl md:text-6xl text-[#2D2D2D] mb-8 leading-tight">
              Path4Change Is Restructuring
            </h1>
            <p className="text-lg md:text-xl text-[#575757] max-w-2xl mx-auto font-light italic">
              Path4Change is currently being simplified, clarified, and prepared for its next chapter. Services are not currently available at this time.
            </p>
          </motion.div>
        </Section>
      </header>

      {/* Main Explanation */}
      <Section>
        <motion.div 
          initial={{ opacity: 0 }} 
          whileInView={{ opacity: 1 }} 
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="prose prose-lg mx-auto text-[#4A4A4A] space-y-6"
        >
          <p>
            Path4Change has been a space for counseling, coaching, personal growth, mindfulness, strengths-based development, Tai Chi, and Qigong-inspired wellness work.
          </p>
          <p>
            At this time, Path4Change is not accepting new counseling, coaching, or wellness clients while the brand is being simplified, clarified, and prepared for its next chapter.
          </p>
          <div className="py-8">
            <blockquote className="border-l-4 border-[#D4C5B9] pl-6 italic text-xl text-[#2D2D2D]">
              "This is not an ending. It is a conscious pause and restructuring process — a chance to step back, refine the mission, and decide what Path4Change is meant to become going forward."
            </blockquote>
          </div>
        </motion.div>
      </Section>

      {/* Current Service Status */}
      <div className="bg-white/50">
        <Section>
          <div className="notice-box">
            <h2 className="text-3xl mb-8 flex items-center gap-3">
              <SafeIcon icon={FiInfo} className="text-[#8C8279]" />
              Current Service Status
            </h2>
            <p className="mb-6 text-[#575757]">At this time, Path4Change is not currently offering:</p>
            <ul className="status-list grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <li>Individual counseling</li>
              <li>Couples counseling</li>
              <li>Positive psychology coaching</li>
              <li>VIA strengths coaching</li>
              <li>Path4Change workshops or courses</li>
              <li>New client consultations</li>
            </ul>
          </div>
        </Section>
      </div>

      {/* Redirect Section */}
      <Section className="text-center">
        <div className="bg-[#F3F1ED] p-10 md:p-16 rounded-3xl">
          <h2 className="text-3xl mb-6">Looking for Tai Chi, Qigong, or Gentle Movement?</h2>
          <p className="text-[#575757] mb-8 max-w-xl mx-auto">
            If you are interested in gentle movement, Tai Chi, Qigong, balance, mobility, healthy aging, or beginner-friendly wellness classes in Tucson, please visit Old Pueblo Tai Chi.
          </p>
          <p className="font-medium text-[#2D2D2D] mb-8">
            Old Pueblo Tai Chi is currently the active home for Dan’s Tai Chi and Qigong-based wellness work.
          </p>
          <a href="https://oldpueblotaichi.com" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2">
            Visit Old Pueblo Tai Chi <SafeIcon icon={FiExternalLink} />
          </a>
        </div>
      </Section>

      {/* Important Notice */}
      <div className="bg-[#FFF5F5]/50 border-y border-[#FEE2E2]">
        <Section>
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="p-3 bg-red-100 text-red-600 rounded-lg">
              <SafeIcon icon={FiAlertTriangle} className="text-2xl" />
            </div>
            <div>
              <h2 className="text-2xl text-red-800 mb-4">Important Notice</h2>
              <p className="text-red-700/80 text-sm leading-relaxed">
                This website is not monitored for urgent mental health needs and does not provide crisis support. If you are experiencing a mental health crisis, emotional distress, or thoughts of self-harm, call or text <strong>988</strong> in the United States to reach the 988 Suicide & Crisis Lifeline. If there is immediate danger, call <strong>911</strong> or go to the nearest emergency room.
              </p>
            </div>
          </div>
        </Section>
      </div>

      {/* Administrative Contact */}
      <Section>
        <div className="text-center space-y-6">
          <h2 className="text-3xl">Administrative Contact</h2>
          <p className="text-[#575757]">
            For administrative questions related to previous Path4Change services, contact Dan Kimball at:
          </p>
          <a href="mailto:dan@path4change.com" className="text-xl text-[#2D2D2D] hover:text-[#8C8279] transition-colors font-medium border-b border-[#D4C5B9] pb-1">
            dan@path4change.com
          </a>
          <p className="text-sm text-[#8C8279] italic mt-4">
            Please note: Path4Change is not currently accepting new clients or service inquiries.
          </p>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-[#2D2D2D] text-[#FAF9F6] py-16">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center text-center">
          <div className="flex items-center gap-2 text-2xl font-serif mb-4">
            <SafeIcon icon={FiHeart} className="text-[#D4C5B9]" />
            Path4Change
          </div>
          <p className="text-[#8C8279] text-sm mb-6 tracking-widest uppercase">Currently Restructuring</p>
          <div className="w-12 h-[1px] bg-[#4A4A4A] mb-6"></div>
          <p className="mt-6 text-[10px] text-[#FAF9F6]/30 tracking-widest uppercase">
            &copy; {new Date().getFullYear()} Path4Change. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;